import React, { type ReactNode } from 'react';

import ModalCore, { CloseButton, type ModalDialogProps } from '@atlaskit/modal-dialog';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { type BackgroundColor, Box, Stack, Text, xcss } from '@atlaskit/primitives';
import { media } from '@atlaskit/primitives/responsive';
import { token } from '@atlaskit/tokens';

import { ModalBody } from './ModalBody';
import { ModalFooter } from './ModalFooter';
import { ModalTitle } from './ModalTitle';

export interface ModalProps extends ModalDialogProps {
	/**
	 * Wrap the actions in a Fragment so that spacing, wrapping, etc are handled by the modal.
	 */
	actions?: ReactNode;
	/** Optional background color for the modal */
	backgroundColor?: BackgroundColor;
	children: ReactNode;
	includeCloseButton?: boolean;
	logo?: ReactNode;
	/**
	 * When set to true, we will not render the `Modal` from atlaskit. Consumers will need to provide
	 * the `width` and `autoFocus` props themselves.
	 *
	 * When using entrypoints (which is almost all use cases for us) the `Modal` is added automatically by the entrypoint.
	 * This is a platform level optimization for entrypoints (to prevent needing to load the modal code). We also do not
	 * want to load a modal inside of a modal as that could cause UI defects.
	 */
	renderedInsideEntrypoint?: boolean;
	/**
	 * @deprecated use sideContent and sideLocation instead.
	 */
	right?: ReactNode;
	sideContent?: ReactNode;
	/**
	 * Defaults to the right side
	 **/
	sideLocation?: 'left' | 'right';
	subtitle?: ReactNode;
	/**
	 * Optional color for the subtitle text. Defaults to 'color.text.subtle'.
	 */
	subtitleColor?: 'color.text' | 'color.text.subtle';
	/**
	 * The normal expectation is that callers provide a string title. The modal will handle default
	 * styling as a heading. But this is typed as a `ReactNode` to allow callers to customise the
	 * title. The modal will still apply its own styling to it, so depending on the particular set of
	 * customisations, your mileage may vary.
	 * See ../../examples/trial-awareness-with-custom-title-example.tsx for an example customisation.
	 */
	title: ReactNode;
}

const hasSideContentContainerStyles = xcss({
	position: 'relative',
	overflowY: 'auto',
	height: '100%',
	borderRadius: 'radius.small',
	[media.above.sm]: {
		display: 'grid',
		gridTemplateColumns: '1fr 1fr',
	},
});

const baseContainerStyles = xcss({
	position: 'relative',
});

const stack = xcss({
	height: '100%',
	padding: 'space.600',
	overflowY: 'auto',
});

const sideContainer = xcss({
	display: 'none',
	[media.above.sm]: {
		backgroundColor: 'color.background.accent.gray.subtlest',
		display: 'flex',
		width: '100%',
		overflowY: 'auto',
	},
});

const leftSideContainer = xcss({
	[media.above.sm]: {
		borderTopLeftRadius: token('radius.xlarge', '3px'),
		borderBottomLeftRadius: token('radius.xlarge', '3px'),
	},
});

const rightSideContainer = xcss({
	[media.above.sm]: {
		borderTopRightRadius: token('radius.xlarge', '3px'),
		borderBottomRightRadius: token('radius.xlarge', '3px'),
	},
});

const closeButtonStyles = xcss({
	position: 'absolute',
	top: 'space.200',
	right: 'space.200',
	zIndex: '1',
});

export const Modal = ({
	logo,
	title,
	subtitle,
	subtitleColor = 'color.text.subtle',
	children,
	actions,
	right,
	sideContent,
	sideLocation = 'right',
	renderedInsideEntrypoint,
	backgroundColor,
	includeCloseButton = false,
	...modalProps
}: ModalProps): React.JSX.Element => {
	const hasSideContent = !!right || !!sideContent;

	const rightContent = right ?? (sideLocation === 'right' ? sideContent : undefined);
	const leftContent = !right && sideLocation === 'left' ? sideContent : undefined;

	const safeOnClose = typeof modalProps.onClose === 'function' ? modalProps.onClose : () => {};

	if (renderedInsideEntrypoint) {
		return (
			<Box
				xcss={[baseContainerStyles, hasSideContent ? hasSideContentContainerStyles : undefined]}
				backgroundColor={backgroundColor}
				style={{ height: modalProps.height }}
			>
				{includeCloseButton && (
					<Box xcss={closeButtonStyles}>
						<CloseButton testId="modal-close-button" onClick={safeOnClose} />
					</Box>
				)}
				{!!leftContent && <Box xcss={[sideContainer, leftSideContainer]}>{leftContent}</Box>}
				<Stack space="space.400" xcss={stack}>
					{logo}
					<Stack space="space.200">
						<ModalTitle>{title}</ModalTitle>
						{subtitle ? (
							<Text color={subtitleColor} size="medium">
								{subtitle}
							</Text>
						) : null}
					</Stack>
					<ModalBody>{children}</ModalBody>
					<ModalFooter>{actions}</ModalFooter>
				</Stack>
				{!!rightContent && <Box xcss={[sideContainer, rightSideContainer]}>{rightContent}</Box>}
			</Box>
		);
	}

	return (
		<ModalCore width={hasSideContent ? 'x-large' : undefined} {...modalProps}>
			<Box
				xcss={[baseContainerStyles, hasSideContent ? hasSideContentContainerStyles : undefined]}
				backgroundColor={backgroundColor}
			>
				{includeCloseButton && (
					<Box xcss={closeButtonStyles}>
						<CloseButton testId="modal-close-button" onClick={safeOnClose} />
					</Box>
				)}
				{!!leftContent && <Box xcss={[sideContainer, leftSideContainer]}>{leftContent}</Box>}
				<Stack space="space.400" xcss={stack}>
					{logo}
					<Stack space="space.200">
						<ModalTitle>{title}</ModalTitle>
						{subtitle ? (
							<Text color={subtitleColor} size="medium">
								{subtitle}
							</Text>
						) : null}
					</Stack>
					<ModalBody>{children}</ModalBody>
					<ModalFooter>{actions}</ModalFooter>
				</Stack>
				{!!rightContent && <Box xcss={[sideContainer, rightSideContainer]}>{rightContent}</Box>}
			</Box>
		</ModalCore>
	);
};
